import { InvoiceExportAlert } from "@/components/shared/alerts/invoice-export-alert";
import type { InvoiceRowData } from "@/components/shared/data-table";
import { StatusSelect } from "@/components/shared/select-inputs/status-select";
import {
  ExportFailedIcon,
  ExportingIcon,
  ProcessingIcon,
  WaitingForClientApprovalIcon,
} from "@/components/shared/status/invoice";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useShouldWaitForClientApproval } from "@/hooks/useShouldWaitForClientApproval";
import {
  InvoiceSmbReviewStatus,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";
import { useUpdateInvoicesStatus } from "@/queries/invoices/update";
import type { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

export const StatusCell = ({ row }: CellContext<InvoiceRowData, unknown>) => {
  const { t } = useTranslation();
  const updateInvoiceStatus = useUpdateInvoicesStatus();
  const shouldWaitForClientApproval = useShouldWaitForClientApproval();

  if (row.original.invoice.status === InvoiceStatus.NEW) {
    return <ProcessingIcon />;
  }

  if (row.original.invoice.status === InvoiceStatus.EXPORTING) {
    return <ExportingIcon />;
  }

  if (row.original.invoice.status === InvoiceStatus.EXPORT_FAILED) {
    return (
      <HoverCard openDelay={100}>
        <HoverCardTrigger>
          <ExportFailedIcon hasWarning />
        </HoverCardTrigger>
        <HoverCardContent side="top" className="w-fit max-w-lg">
          <div className="flex text-start">
            <InvoiceExportAlert
              invoice={{
                errorReason: row.original.invoice.errorReason,
                exports: row.original.exports,
                validationErrors: row.original.invoice.validationErrors,
              }}
            />
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  if (
    shouldWaitForClientApproval &&
    row.original.invoice.status === InvoiceStatus.REVIEWED &&
    row.original.invoice.smbReviewStatus === InvoiceSmbReviewStatus.UNREVIEWED
  ) {
    return (
      <HoverCard openDelay={100}>
        <HoverCardTrigger>
          <WaitingForClientApprovalIcon />
        </HoverCardTrigger>
        <HoverCardContent side="top" className="w-fit">
          <div className="flex flex-col text-start">
            <strong>{t("invoice_statuses.waiting_for_client_approval")}</strong>
            <p>{t("waiting_for_client_approval_description")}</p>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  const status = row.original.invoice.status;

  const handleCheckedChange = async (value: InvoiceStatus) => {
    await updateInvoiceStatus([row.original.invoice.invoiceId], value);
  };

  const tooltip = row.original.invoice.errorReason
    ? t(
        `pipeline_errors.${row.original.invoice.errorReason.code}`,
        row.original.invoice.errorReason?.data ?? {},
      )
    : t(`invoice_statuses.${status}`);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div onClick={(event) => event.stopPropagation()}>
            <StatusSelect status={status} onSelect={handleCheckedChange} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip as string}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
